import { z } from 'zod'

import { GetCompanyPaymentTermsSchema } from './payment-terms.ts'
import { GetStatusSchema } from './status.ts'

export const CompanyTypeAliasSchema = z.enum(['shipper', 'haulier', 'broker', 'other'])

export const GetCompanyType = z.object({
  alias: CompanyTypeAliasSchema,
  title: z.string(),
})

export const GetCompanyCountrySchema = z.object({
  code: z.string().nullish(),
  emoji: z.emoji().nullish(),
  name: z.string().nullish(),
  phone_code: z.string().nullish(),
})

export const GetCompanySchema = z.object({
  created_at: z.coerce.date(),
  email: z.email().nullish(),
  hubspot_link: z.url().nullish(),
  hx_id: z.string().nullish(),
  id: z.number(),
  is_marketplace: z.boolean(),
  notes: z.string().nullish(),
  organisation_id: z.number().nullish(),
  phone: z.string().nullish(),
  phone_country: z.string().length(2).nullish(),
  phone_formatted: z.string().nullish(),
  registration_number: z.string().nullish(),
  represents_organisation_id: z.number().nullish(),
  short_title: z.string().nullish(),
  slug: z.string()?.nullish(),
  teleroute_id: z.string().nullish(),
  timocom_id: z.string().nullish(),
  title: z.string(),
  trans_id: z.string().nullish(),
  vat: z.string().nullish(),
  website: z.url().nullish(),
  xero_contact_url: z.url().nullish(),
})

/** How often a company appears on loads, recalculated shortly after the writes that change it. */
export const GetCompanyStatisticsSchema = z.object({
  carrier_jobs_count: z.number(),
  contacts_count: z.number(),
  customer_loads_count: z.number(),
  locations_count: z.number(),
  updated_at: z.string().nullish(),
})

/** One company from `GET /companies/search`, ranked by the API's search index. */
export const GetCompanySearchResultSchema = z.object({
  address: z
    .object({
      address: z.string().nullish(),
      address_second_line: z.string().nullish(),
      city: z.string().nullish(),
      postcode: z.string().nullish(),
    })
    .nullish(),
  /** ISO 3166-1 alpha-2 code. */
  country: z.string().nullish(),
  id: z.number(),
  is_marketplace: z.boolean(),
  organisation: z.object({ slug: z.string() }).nullish(),
  organisation_id: z.number().nullish(),
  represents_organisation_id: z.number().nullish(),
  risk_profile: z
    .object({
      score: z.string().nullish(),
      valid_to: z.string().nullish(),
    })
    .nullish(),
  statistics: GetCompanyStatisticsSchema.omit({ updated_at: true }),
  title: z.string(),
  type: CompanyTypeAliasSchema.nullish(),
  vat: z.string().nullish(),
  verified_at: z.string().nullish(),
  xero_synced: z.boolean(),
})

export const GetCompanyAccountSchema = z.object({
  billing_address: z.string().nullish(),
  billing_address_second_line: z.string().nullish(),
  billing_city: z.string().nullish(),
  billing_postcode: z.string().nullish(),
  // Optional on purpose: an old backend without CK-2016 omits the property
  // entirely (unsupported), while a new backend returns an object whose
  // fields may all be null (supported but unconfigured).
  carrier_payment_terms: GetCompanyPaymentTermsSchema.nullish(),
  email: z.email().nullish(),
  notes: z.string().nullish(),
  phone: z.string().nullish(),
  phone_country: z.string().length(2).nullish(),
  phone_formatted: z.string().nullish(),
})

export const GetCompanyProfileSchema = z.object({
  adr: z.boolean().nullish(),
  country: z
    .object({
      alias: z.string().nullish(),
      id: z.number().nullish(),
      title: z.string().nullish(),
    })
    .nullish(),
  country_preferences: z.array(z.string()).nullish(),
  curtain: z.boolean().nullish(),
  dvs: z.boolean().nullish(),
  eta_requirement: z
    .object({
      alias: z.string().nullish(),
      id: z.number().nullish(),
      title: z.string().nullish(),
    })
    .nullish(),
  fridge: z.boolean().nullish(),
  load_types: z
    .array(
      z.object({
        id: z.number().nullish(),
        slug: z.string().nullish(),
        title: z.string().nullish(),
      }),
    )
    .nullish(),
  max_weight: z.number().nullish(),
  mega: z.boolean().nullish(),
  pod_requirement: z
    .object({
      alias: z.string().nullish(),
      id: z.number().nullish(),
      title: z.string().nullish(),
    })
    .nullish(),
  pod_requirement_description: z.string().nullish(),
  status: GetStatusSchema,
  uk_region_preferences: z.array(z.string()).nullish(),
  waste: z.boolean().nullish(),
  weight_max: z.number().nullish(),
  weight_min: z.number().nullish(),
})

export const GetHaulierProfileSchema = z.object({
  carrier: z.boolean().nullish(),
  country_preferences: z.array(z.string()).nullish(),
  extras: z
    .object({
      adr: z.boolean().nullish(),
      coil: z.boolean().nullish(),
      crane: z.boolean().nullish(),
      double_decker: z.boolean().nullish(),
      dvs: z.boolean().nullish(),
      moffet: z.boolean().nullish(),
      tail_lift: z.boolean().nullish(),
      walking_floor: z.boolean().nullish(),
      waste: z.boolean().nullish(),
    })
    .nullish(),
  forwarder: z.boolean().nullish(),
  load_types: z
    .array(
      z.object({
        id: z.number().nullish(),
        slug: z.string().nullish(),
        title: z.string().nullish(),
      }),
    )
    .nullish(),
  max_weight: z.number().nullish(),
  min_weight: z.number().nullish(),
  risk_rate: z.number().nullish(),
  status: GetStatusSchema.nullish(),
  uk_region_preferences: z.array(z.string()).nullish(),
})

export const GetCompanyAddressSchema = z.object({
  address: z.string().nullish(),
  address_second_line: z.string().nullish(),
  city: z.string().nullish(),
  country: GetCompanyCountrySchema.nullish(),
  postcode: z.string().nullish(),
  region: z.string().nullish(),
})

export const GetCompanyFinancialsSchema = z.object({
  credit_limit: z.number().nullish(),
  total_profit: z.coerce.number().nullish(),
  total_spend: z.coerce.number().nullish(),
})

export const GetCompanyXeroAttributesSchema = z.object({
  merged_to_company: z.object({ id: z.number(), title: z.string() }).nullish(),
  merged_to_xero_contact_id: z.string().nullish(),
  merged_to_xero_contact_url: z.string().url().nullish(),
  outstanding_balance: z.number().nullish(),
  overdue_outstanding_balance: z.number().nullish(),
  overdue_payable_balance: z.number().nullish(),
  payable_balance: z.number().nullish(),
  payment_terms_day: z.number().int().nullish(),
  payment_terms_type: z
    .enum(['DAYSAFTERBILLDATE', 'DAYSAFTERBILLMONTH', 'OFCURRENTMONTH', 'OFFOLLOWINGMONTH'])
    .nullish(),
  xero_contact_id: z.string().nullish(),
  xero_contact_url: z.string().url().nullish(),
  xero_status: z.string().nullish(),
  xero_synced_at: z.coerce.date().nullish(),
})

export const GetCompanyLatestMemberLoginSchema = z.object({
  logged_at: z.coerce.date(),
})
